import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Icon,
  Collapse,
  useDisclosure,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  OrderedList,
  UnorderedList,
  ListItem,
  Code,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
  Link
} from '@chakra-ui/react';
import {
  BookOpen,
  Play,
  ChevronDown,
  ChevronRight,
  Search,
  HelpCircle,
  Zap,
  Target,
  Shield,
  TrendingUp,
  DollarSign,
  BarChart3,
  Settings,
  Lightbulb,
  AlertTriangle,
  CheckCircle,
  Info,
  ExternalLink,
  Video,
  FileText,
  MessageCircle
} from 'lucide-react';

interface KnowledgeBaseProps {
  isOpen: boolean;
  onClose: () => void;
}

const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');

  const categories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Play,
      color: 'green',
      description: 'New to RWA investing? Start here!'
    },
    {
      id: 'rwa-basics',
      title: 'RWA Basics',
      icon: BookOpen,
      color: 'blue',
      description: 'Understanding Real-World Assets'
    },
    {
      id: 'platform-guide',
      title: 'Platform Guide',
      icon: Settings,
      color: 'purple',
      description: 'How to use Alpha Playbook'
    },
    {
      id: 'backtesting',
      title: 'Backtesting',
      icon: BarChart3,
      color: 'orange',
      description: 'Test your strategies'
    },
    {
      id: 'trading',
      title: 'Trading',
      icon: TrendingUp,
      color: 'teal',
      description: 'Execute trades safely'
    },
    {
      id: 'troubleshooting',
      title: 'Help & FAQ',
      icon: HelpCircle,
      color: 'red',
      description: 'Common issues & solutions'
    }
  ];

  const gettingStartedContent = [
    {
      title: '🚀 Welcome to Alpha Playbook!',
      type: 'overview',
      content: {
        description: 'Alpha Playbook is your gateway to Real-World Asset (RWA) investing. This platform helps you discover, analyze, and invest in tokenized real-world assets like Treasury bills, credit, and yield products.',
        keyPoints: [
          'Discover emerging RWA opportunities',
          'Backtest strategies with real market data',
          'Execute trades with confidence',
          'Track your portfolio performance'
        ]
      }
    },
    {
      title: '📋 Step 1: Understanding the Dashboard',
      type: 'tutorial',
      content: {
        steps: [
          {
            step: 'Navigation Tabs',
            description: 'Use the top navigation to switch between Discovery, Analytics, PatchTST AI, CashFlow Beast, and RWA Playbook',
            tip: 'Start with RWA Playbook for signal analysis'
          },
          {
            step: 'Status Panel',
            description: 'Green status means all systems are running. If you see errors, check the troubleshooting section',
            tip: 'API must be running for signals to work'
          },
          {
            step: 'Signal Cards',
            description: 'Each card shows a trading signal with confidence level, target price, and market context',
            tip: 'Higher confidence signals (>85%) are more reliable'
          }
        ]
      }
    },
    {
      title: '🎯 Step 2: Your First Signal Analysis',
      type: 'hands-on',
      content: {
        objective: 'Analyze your first RWA trading signal',
        steps: [
          'Click on the RWA Playbook tab',
          'Look for signals with HIGH confidence (green badge)',
          'Click "View Details" on any signal card',
          'Review the signal strength, target price, and market context',
          'Click "Run Backtest" to see historical performance'
        ],
        expected: 'You should see detailed analysis and backtesting results'
      }
    },
    {
      title: '📊 Step 3: Running Your First Backtest',
      type: 'hands-on',
      content: {
        objective: 'Test a strategy with historical data',
        steps: [
          'From any signal, click "Run Backtest"',
          'In the modal, select your preferred assets (ONDO, CFG, VBILL recommended for beginners)',
          'Choose "SMA Momentum" strategy (safest for beginners)',
          'Set initial capital to $10,000',
          'Click "Run Advanced RWA Backtest"',
          'Review the results: Total Return, Sharpe Ratio, Max Drawdown'
        ],
        expected: 'A complete performance analysis with charts and metrics'
      }
    }
  ];

  const rwaBasicsContent = [
    {
      title: '🏛️ What are Real-World Assets (RWAs)?',
      type: 'explanation',
      content: {
        definition: 'RWAs are traditional financial assets that have been tokenized on blockchain networks. This includes Treasury bills, corporate bonds, real estate, commodities, and credit products.',
        examples: [
          {
            asset: 'ONDO (Ondo Finance)',
            description: 'Tokenized yield products and Treasury bills',
            tvl: '$1.5B+ TVL',
            riskLevel: 'Medium'
          },
          {
            asset: 'CFG (Centrifuge)',
            description: 'Real-world credit and invoice financing',
            tvl: '$200M+ TVL',
            riskLevel: 'Medium-High'
          },
          {
            asset: 'VBILL (VanEck)',
            description: 'Tokenized Treasury bills',
            tvl: '$150M+ TVL',
            riskLevel: 'Low'
          }
        ]
      }
    },
    {
      title: '💡 Why RWAs Matter',
      type: 'explanation',
      content: {
        benefits: [
          '24/7 trading of traditional assets',
          'Lower barriers to entry ($100 vs $10,000)',
          'Programmable compliance and automation',
          'Global accessibility',
          'Transparent on-chain settlement'
        ],
        marketSize: '$24B+ Total Value Locked (Feb 2026)',
        growth: '+14% monthly growth rate'
      }
    },
    {
      title: '🎯 RWA Investment Categories',
      type: 'categorized',
      content: [
        {
          category: 'Treasury & Yield',
          description: 'Low-risk government-backed securities',
          examples: ['ONDO USDY', 'BlackRock BUIDL', 'VBILL'],
          riskLevel: 'Low',
          expectedYield: '4-6% APY'
        },
        {
          category: 'Credit & Lending',
          description: 'Corporate credit and invoice financing',
          examples: ['Centrifuge pools', 'Maple Finance', 'TrueFi'],
          riskLevel: 'Medium',
          expectedYield: '6-12% APY'
        },
        {
          category: 'Real Estate',
          description: 'Tokenized property investments',
          examples: ['RealT', 'Lofty', 'Roofstock onChain'],
          riskLevel: 'Medium-High',
          expectedYield: '8-15% APY'
        }
      ]
    }
  ];

  const platformGuideContent = [
    {
      title: '🖥️ Platform Overview',
      type: 'feature-tour',
      content: {
        sections: [
          {
            name: 'Discovery Tab',
            purpose: 'Find new crypto opportunities and market trends',
            whenToUse: 'Daily market scanning'
          },
          {
            name: 'Analytics Tab',
            purpose: 'Deep dive analysis on specific assets',
            whenToUse: 'Before making investment decisions'
          },
          {
            name: 'RWA Playbook',
            purpose: 'RWA-specific signals and backtesting',
            whenToUse: 'RWA strategy development'
          },
          {
            name: 'CashFlow Beast',
            purpose: 'Automated trading and portfolio management',
            whenToUse: 'When ready for automation'
          }
        ]
      }
    },
    {
      title: '📊 Understanding Signals',
      type: 'detailed-guide',
      content: {
        signalTypes: [
          {
            type: 'BUY Signal',
            meaning: 'Algorithm suggests this asset may increase in value',
            confidence: 'Look for >85% confidence levels',
            color: 'Green badge'
          },
          {
            type: 'HOLD Signal',
            meaning: 'Current position should be maintained',
            confidence: 'Medium confidence typically',
            color: 'Yellow badge'
          },
          {
            type: 'SELL Signal',
            meaning: 'Algorithm suggests taking profits or cutting losses',
            confidence: 'High confidence sells are priority',
            color: 'Red badge'
          }
        ],
        confidenceLevels: {
          'High (>85%)': 'Strong algorithmic conviction - safest to follow',
          'Medium (70-85%)': 'Moderate conviction - combine with other analysis',
          'Low (<70%)': 'Weak signal - wait for better opportunities'
        }
      }
    },
    {
      title: '🔧 Customizing Your Experience',
      type: 'settings-guide',
      content: {
        filters: [
          'Confidence threshold adjustment',
          'Asset type filtering (RWA, DeFi, CEX tokens)',
          'Time horizon selection (short, medium, long-term)',
          'Portfolio allocation preferences'
        ],
        notifications: [
          'High-confidence signal alerts',
          'Portfolio rebalancing reminders',
          'Market volatility warnings',
          'New asset addition notifications'
        ]
      }
    }
  ];

  const backtestingContent = [
    {
      title: '📈 Backtesting Fundamentals',
      type: 'educational',
      content: {
        definition: 'Backtesting simulates how your trading strategy would have performed using historical market data.',
        importance: [
          'Validate strategy effectiveness',
          'Understand risk characteristics',
          'Optimize parameters',
          'Build confidence before live trading'
        ],
        limitations: [
          'Past performance ≠ future results',
          'Market conditions change',
          'Execution costs not always accurate',
          'Survivorship bias in data'
        ]
      }
    },
    {
      title: '🛠️ Alpha Playbook Backtesting Features',
      type: 'feature-guide',
      content: {
        strategies: [
          {
            name: 'SMA Momentum',
            description: 'Moving average crossover strategy',
            bestFor: 'Trending markets, beginners',
            parameters: 'Short SMA (10), Long SMA (50)'
          },
          {
            name: 'RSI Strategy',
            description: 'Relative Strength Index based entries',
            bestFor: 'Range-bound markets',
            parameters: 'Buy threshold (60), Sell threshold (40)'
          },
          {
            name: 'RWA Weighted',
            description: 'Portfolio weighted by market leaders',
            bestFor: 'Diversified RWA exposure',
            parameters: 'ETH 30%, ONDO 25%, others distributed'
          }
        ],
        advancedFeatures: [
          'Monte Carlo simulation (100-500 runs)',
          'Benchmark comparison (SPY, BLK, ETH)',
          'Drawdown analysis',
          'Risk-adjusted metrics (Sharpe ratio)',
          'Export results to CSV'
        ]
      }
    },
    {
      title: '📋 Step-by-Step Backtesting',
      type: 'tutorial',
      content: {
        steps: [
          {
            step: '1. Select Assets',
            details: 'Choose 3-5 RWA assets. Recommended starter portfolio: ONDO, CFG, VBILL, ETH, BLK',
            tip: 'Mix low-risk (VBILL) with medium-risk (ONDO) assets'
          },
          {
            step: '2. Choose Strategy',
            details: 'Start with SMA Momentum for simplicity',
            tip: 'Beginners should avoid complex strategies initially'
          },
          {
            step: '3. Set Parameters',
            details: 'Use default values initially: SMA Short=10, Long=50, Threshold=2.0',
            tip: 'Lower thresholds = more signals, higher = fewer but higher quality'
          },
          {
            step: '4. Enable Monte Carlo',
            details: 'Check the Monte Carlo box for risk analysis',
            tip: '100 simulations provide good balance of speed vs accuracy'
          },
          {
            step: '5. Review Results',
            details: 'Focus on Sharpe ratio (>1 is good), Max drawdown (<20% preferred), Win rate (>60% excellent)',
            tip: 'High returns with low drawdown indicate robust strategy'
          }
        ]
      }
    }
  ];

  const troubleshootingContent = [
    {
      title: '🚨 Common Issues & Solutions',
      type: 'troubleshooting',
      content: [
        {
          issue: 'RWA Signal Loading Issue / NetworkError',
          causes: [
            'API server not running',
            'Port conflicts',
            'Firewall blocking connections',
            'CORS issues'
          ],
          solutions: [
            'Check if API server is running on port 3001',
            'Restart both frontend and backend servers',
            'Clear browser cache and refresh',
            'Disable browser extensions temporarily',
            'Check Windows Firewall settings'
          ],
          quickFix: 'Press Ctrl+F5 to hard refresh the page'
        },
        {
          issue: 'Backtest Not Running',
          causes: [
            'Python dependencies missing',
            'Invalid date ranges',
            'Unsupported asset symbols',
            'Memory issues with large datasets'
          ],
          solutions: [
            'Run: pip install pandas numpy yfinance',
            'Ensure end_date > start_date',
            'Use only supported symbols: ONDO, CFG, VBILL, BLK, ETH, HBAR, USDC',
            'Reduce date range or number of assets',
            'Lower Monte Carlo simulation count'
          ]
        },
        {
          issue: 'No Trading Signals Appearing',
          causes: [
            'Confidence threshold too high',
            'No market data available',
            'Time horizon filter too restrictive'
          ],
          solutions: [
            'Lower confidence threshold to 70%',
            'Check internet connection',
            'Reset filters to default settings',
            'Wait for next market update cycle'
          ]
        }
      ]
    },
    {
      title: '💬 Getting Help',
      type: 'support',
      content: {
        selfHelp: [
          'Check this knowledge base first',
          'Review terminal output for error messages',
          'Try the troubleshooting steps above',
          'Restart the application'
        ],
        whenToSeekHelp: [
          'Error persists after troubleshooting',
          'Unexpected trading behavior',
          'Data accuracy concerns',
          'Feature requests'
        ],
        supportChannels: [
          'GitHub Issues (for bugs)',
          'Documentation wiki',
          'Community Discord',
          'Email support'
        ]
      }
    }
  ];

  const getCurrentContent = () => {
    switch (activeCategory) {
      case 'getting-started':
        return gettingStartedContent;
      case 'rwa-basics':
        return rwaBasicsContent;
      case 'platform-guide':
        return platformGuideContent;
      case 'backtesting':
        return backtestingContent;
      case 'troubleshooting':
        return troubleshootingContent;
      default:
        return gettingStartedContent;
    }
  };

  const renderContent = (item: any) => {
    switch (item.type) {
      case 'overview':
        return (
          <Box>
            <Text mb={4}>{item.content.description}</Text>
            <Text fontWeight="semibold" mb={2}>Key Features:</Text>
            <UnorderedList>
              {item.content.keyPoints.map((point: string, idx: number) => (
                <ListItem key={idx}>{point}</ListItem>
              ))}
            </UnorderedList>
          </Box>
        );

      case 'tutorial':
        return (
          <VStack align="stretch" spacing={4}>
            {item.content.steps.map((step: any, idx: number) => (
              <Box key={idx} p={4} bg="blue.50" borderRadius="lg" borderLeft="4px solid" borderColor="blue.400">
                <Text fontWeight="semibold" color="blue.800">{step.step}</Text>
                <Text mt={2} color="blue.700">{step.description}</Text>
                {step.tip && (
                  <Alert status="info" mt={2} variant="subtle" size="sm">
                    <AlertIcon />
                    <Text fontSize="sm">💡 {step.tip}</Text>
                  </Alert>
                )}
              </Box>
            ))}
          </VStack>
        );

      case 'hands-on':
        return (
          <Box>
            <Alert status="success" mb={4} variant="subtle">
              <AlertIcon />
              <Box>
                <AlertTitle>Objective:</AlertTitle>
                <AlertDescription>{item.content.objective}</AlertDescription>
              </Box>
            </Alert>
            
            <Text fontWeight="semibold" mb={3}>Steps:</Text>
            <OrderedList spacing={2}>
              {item.content.steps.map((step: string, idx: number) => (
                <ListItem key={idx}>{step}</ListItem>
              ))}
            </OrderedList>
            
            <Box mt={4} p={3} bg="green.50" borderRadius="lg">
              <Text fontWeight="semibold" color="green.800">Expected Result:</Text>
              <Text color="green.700">{item.content.expected}</Text>
            </Box>
          </Box>
        );

      case 'troubleshooting':
        return (
          <VStack spacing={6} align="stretch">
            {item.content.map((issue: any, idx: number) => (
              <Box key={idx} p={5} bg="red.50" borderRadius="lg" borderLeft="4px solid" borderColor="red.400">
                <Heading size="sm" color="red.800" mb={3}>{issue.issue}</Heading>
                
                <Box mb={4}>
                  <Text fontWeight="semibold" color="red.700" mb={2}>Common Causes:</Text>
                  <UnorderedList>
                    {issue.causes.map((cause: string, causeIdx: number) => (
                      <ListItem key={causeIdx} color="red.600">{cause}</ListItem>
                    ))}
                  </UnorderedList>
                </Box>
                
                <Box mb={4}>
                  <Text fontWeight="semibold" color="red.700" mb={2}>Solutions:</Text>
                  <OrderedList>
                    {issue.solutions.map((solution: string, solIdx: number) => (
                      <ListItem key={solIdx} color="red.600">{solution}</ListItem>
                    ))}
                  </OrderedList>
                </Box>
                
                {issue.quickFix && (
                  <Alert status="warning" variant="subtle" size="sm">
                    <AlertIcon />
                    <Text fontSize="sm"><strong>Quick Fix:</strong> {issue.quickFix}</Text>
                  </Alert>
                )}
              </Box>
            ))}
          </VStack>
        );

      default:
        return <Text>{JSON.stringify(item.content, null, 2)}</Text>;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={BookOpen} color="purple.500" boxSize={6} />
            <Text>Alpha Playbook Knowledge Base</Text>
            <Badge colorScheme="purple" variant="subtle">Beginner-Friendly Guide</Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            
            {/* Search Bar */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={Search} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search for help, tutorials, or guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="gray.50"
              />
            </InputGroup>

            <HStack align="start" spacing={6}>
              {/* Categories Sidebar */}
              <VStack spacing={3} minW="250px" align="stretch">
                <Text fontWeight="bold" color="gray.700">Categories</Text>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    leftIcon={<Icon as={category.icon} />}
                    variant={activeCategory === category.id ? "solid" : "ghost"}
                    colorScheme={activeCategory === category.id ? category.color : "gray"}
                    justifyContent="flex-start"
                    onClick={() => setActiveCategory(category.id)}
                    size="sm"
                  >
                    <VStack align="start" spacing={0}>
                      <Text>{category.title}</Text>
                      <Text fontSize="xs" opacity={0.8}>{category.description}</Text>
                    </VStack>
                  </Button>
                ))}
              </VStack>

              {/* Content Area */}
              <Box flex={1}>
                <VStack spacing={6} align="stretch">
                  {getCurrentContent().map((item, idx) => (
                    <Card key={idx}>
                      <CardHeader pb={2}>
                        <Heading size="md">{item.title}</Heading>
                      </CardHeader>
                      <CardBody>
                        {renderContent(item)}
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </Box>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="purple" onClick={onClose}>
            Got it! Let's go
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default KnowledgeBase;